import type { JWTPayload } from "@/lib/auth/auth.types";
import { useAuth } from "@/lib/contexts/auth.context";
import UserService from "@/lib/user.service";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { toast } from "sonner";

interface ProfileFormData {
	email: string;
	displayName: string;
}

interface UseProfileFormProps {
	onSave?: () => void;
}

/**
 * Hook for the profile form component
 *
 * This hook is used to update the user's profile information
 *
 * exposes the following:
 * - formData: ProfileFormData - The form data
 * - isLoading: boolean - Whether the form is loading
 * - showSuccessMessage: boolean - Whether the form has been saved successfully
 * - handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void - The function to handle the change of the form
 * - handleSubmit: (e: React.FormEvent) => Promise<void> - The function to handle the submission of the form
 *
 * @param onSave - Optional callback function to be called after successful save
 */
export function useProfileForm({ onSave }: UseProfileFormProps = {}) {
	const [cookies] = useCookies(["jwt_token"]);
	const { logout, refreshUserAndToken } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState<ProfileFormData>({
		email: "",
		displayName: "",
	});
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);

	// Load user data from JWT token
	useEffect(() => {
		if (cookies.jwt_token) {
			try {
				const payload = jwtDecode<JWTPayload>(cookies.jwt_token);
				setFormData({
					email: payload.email || "",
					displayName: payload.displayName || "",
				});
			} catch (error) {
				console.error("Failed to decode JWT token:", error);
				toast.error("Fehler beim Laden der Benutzerdaten");
			}
		}
	}, [cookies.jwt_token]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setShowSuccessMessage(false);

		try {
			// Get the auth service instance
			const userService = UserService.getInstance();

			// Get the current email and name from JWT token
			const payload = jwtDecode<JWTPayload>(cookies.jwt_token);
			const currentEmail = payload.email || "";
			const currentName = payload.displayName || "";

			let shouldLogout = false;

			// Only update if the email has changed
			if (formData.email !== currentEmail) {
				// Update the user's email in Firebase
				const success = await userService.updateUserEmail(formData.email);
				if (success) {
					shouldLogout = true;
				} else {
					toast.error("Fehler beim Aktualisieren der Email-Adresse");
				}
			}

			// Only update if the name has changed
			if (formData.displayName !== currentName) {
				// Update the user's display name in Firebase
				await userService.updateUserDisplayName(formData.displayName);

				// refresh the user
				await refreshUserAndToken();
				toast.success("Benutzerdaten erfolgreich aktualisiert");
			}

			if (shouldLogout) {
				setShowSuccessMessage(true);
				logout();
			}

			if (onSave) {
				onSave();
			}
		} catch (error) {
			console.error("Failed to update profile:", error);
			toast.error("Fehler beim Aktualisieren des Profils");
		} finally {
			setIsLoading(false);
		}
	};

	return {
		formData,
		isLoading,
		showSuccessMessage,
		handleChange,
		handleSubmit,
	};
}
